import { useAuth, useUserType } from "../hooks";
import { useInvestorModuleAuth } from "../hooks/useInvestorModuleAuth";
import { Navigate, useLocation } from "react-router-dom";
import {
  canAccessInvestorModule,
  canAccessMemberModule,
  isInvestorPath,
  isInvestorPublicPath,
  isInvestorStaffPreviewPath,
  isMemberAreaPath,
  isStaffUserType,
  normalizeUserType,
} from "./investorAccess";
import { getInvestorQueryParams, withInvestorQuery } from "./investorRouteHelpers";
import {
  canAccessRedirectPath,
  resolvePostCoopLoginPath,
  resolveUnifiedPostLoginPath,
} from "./postAuthRedirect";

const COOPERATIVE_PUBLIC = [
  "/login",
  "/reset_password",
  "/forget_password",
  "/member-signup",
  "/guarantor/confirm",
  "/member/payment/confirmation",
  "/member/payment/alatpay",
  "/invest/payment/confirmation",
  "/invest/marketplace/payment/confirmation",
  "/support/session",
];

const normalizePath = (pathname) => pathname.replace(/\/$/, "") || "/";

export const AuthGuard = ({ children }) => {
  const path = useLocation().pathname;
  const location = useLocation();
  const normalizedPath = normalizePath(path);
  const auth = useAuth();
  const userType = useUserType();
  const { isLoggedIn: isInvestorModuleLoggedIn, isStaff } = useInvestorModuleAuth();

  const isCoopLoggedIn = !!auth;
  const preservedQuery = getInvestorQueryParams(location.search);

  if (normalizedPath === "/member-signup") {
    return <>{children}</>;
  }

  if (path.startsWith("/guarantor/confirm")) {
    return <>{children}</>;
  }

  if (path.startsWith("/member/payment/confirmation")) {
    return <>{children}</>;
  }

  if (path.startsWith("/member/payment/alatpay")) {
    return <>{children}</>;
  }

  if (path.startsWith("/invest/payment/confirmation")) {
    return <>{children}</>;
  }

  if (path.startsWith("/invest/marketplace/payment/confirmation")) {
    return <>{children}</>;
  }

  if (normalizedPath === "/support/session") {
    return <>{children}</>;
  }

  if (normalizedPath === "/login") {
    if (isCoopLoggedIn) {
      if (preservedQuery.redirect && canAccessRedirectPath(userType, preservedQuery.redirect)) {
        return <Navigate to={withInvestorQuery(preservedQuery.redirect, preservedQuery)} replace />;
      }

      const type = normalizeUserType(userType);
      if (type === "investor") {
        return <Navigate to="/invest/dashboard" replace />;
      }
      if (canAccessMemberModule(userType)) {
        return <Navigate to="/member/dashboard" replace />;
      }
      if (isStaffUserType(userType)) {
        return <Navigate to="/super_admin/dashboard" replace />;
      }
    }
  }

  if (isInvestorPublicPath(normalizedPath)) {
    if (normalizedPath === "/invest/login") {
      return <Navigate to={withInvestorQuery("/login", preservedQuery)} replace />;
    }

    // Payment confirmation must stay public even when logged in (Paystack return).
    if (normalizedPath.startsWith("/invest/payment/confirmation")) {
      return <>{children}</>;
    }

    if (isInvestorModuleLoggedIn) {
      if (isStaff) {
        return <Navigate to="/super_admin/dashboard" replace />;
      }
      if (canAccessInvestorModule(userType)) {
        return <Navigate to="/invest/dashboard" replace />;
      }
    }
    return <>{children}</>;
  }

  if (isInvestorPath(normalizedPath)) {
    const staffPreview =
      isInvestorStaffPreviewPath(normalizedPath, location.search) &&
      isStaff &&
      isCoopLoggedIn;

    if (staffPreview) {
      return <>{children}</>;
    }

    if (!isInvestorModuleLoggedIn) {
      const loginTarget = withInvestorQuery("/login", {
        ...preservedQuery,
        redirect: preservedQuery.redirect || path,
      });
      return <Navigate to={loginTarget} replace />;
    }
    if (isStaff) {
      return <Navigate to="/super_admin/dashboard" replace />;
    }
    if (!canAccessInvestorModule(userType)) {
      return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
  }

  if (
    isCoopLoggedIn &&
    normalizeUserType(userType) === "investor" &&
    isMemberAreaPath(normalizedPath)
  ) {
    return <Navigate to="/invest/dashboard" replace />;
  }

  const isCooperativePrivate = ["super_admin", "member"].some((route) =>
    path.includes(route)
  );

  if (isCooperativePrivate && !isCoopLoggedIn) {
    const loginTarget = withInvestorQuery("/login", {
      redirect: path,
    });
    return <Navigate to={loginTarget} replace />;
  }

  if (!isCooperativePrivate && isCoopLoggedIn && isStaffUserType(userType)) {
    return <Navigate to="/super_admin/dashboard" replace />;
  }

  if (
    COOPERATIVE_PUBLIC.includes(normalizedPath) &&
    isCoopLoggedIn &&
    canAccessMemberModule(userType) &&
    !path.startsWith("/member/payment/confirmation") &&
    !path.startsWith("/member/payment/alatpay") &&
    !path.startsWith("/invest/payment/confirmation") &&
    !path.startsWith("/invest/marketplace/payment/confirmation")
  ) {
    return <Navigate to={resolvePostCoopLoginPath(userType, preservedQuery)} replace />;
  }

  return <>{children}</>;
};
